import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverBudgetGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-on-a-budget";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "free-lowcost": "Free & Low-Cost Attractions",
    "budget-food": "Budget-Friendly Food",
    "outdoor-activities": "Outdoor Activities",
    "transport": "Getting Around",
    "seasonal": "Seasonal Tips",
    "itineraries": "Suggested Itineraries",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Fill with relevant references from the conversation.
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver (Official Tourism Site)",
    },
    {
      url: "https://www.translink.ca/",
      label: "TransLink (Public Transportation Info)",
    },
    {
      url: "https://vancouver.ca/",
      label: "City of Vancouver Official Site",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // NOTE: Each location object’s "id" must match the
  // ref for the actual <p> or <li> element in the text.
  const locations = [
    {
      id: "stanley-park",
      lat: 49.3000,
      lng: -123.1400,
      popupText: "Stanley Park, Vancouver",
    },
    {
      id: "granville-island",
      lat: 49.2726,
      lng: -123.1352,
      popupText: "Granville Island Public Market",
    },
    {
      id: "gastown-clock",
      lat: 49.2847,
      lng: -123.1110,
      popupText: "Gastown Steam Clock",
    },
    {
      id: "english-bay",
      lat: 49.2863,
      lng: -123.1436,
      popupText: "English Bay Beach",
    },
    {
      id: "queen-elizabeth-park",
      lat: 49.2417,
      lng: -123.1128,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "bon-off-broadway",
      lat: 49.2643,
      lng: -123.0568,
      popupText: "Bon’s Off Broadway Diner",
    },
    {
      id: "warehouse",
      lat: 49.2802,
      lng: -123.1211,
      popupText: "The Famous Warehouse Restaurant",
    },
    {
      id: "lynn-canyon",
      lat: 49.3437,
      lng: -123.0180,
      popupText: "Lynn Canyon Suspension Bridge",
    },
    {
      id: "grouse-base",
      lat: 49.3793,
      lng: -123.0828,
      popupText: "Grouse Mountain Base",
    },
    {
      id: "airport-canada-line",
      lat: 49.1947,
      lng: -123.1770,
      popupText: "YVR Airport - Canada Line Station",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver on a Budget: Must-Do’s</title>
        <meta
          name="description"
          content="Learn how to explore Vancouver on a budget. Discover free attractions, cheap eats, transportation tips, and more."
        />
        <meta
          name="keywords"
          content="Vancouver budget travel, free attractions Vancouver, cheap eats Vancouver, public transit Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-on-a-budget.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver on a Budget: Must-Do’s"
        />
        <meta
          property="og:description"
          content="Discover how to see Vancouver’s highlights without breaking the bank!"
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-on-a-budget.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-on-a-budget.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver on a Budget: Must-Do’s"
        />
        <meta
          name="twitter:description"
          content="Your guide to exploring Vancouver affordably, featuring free attractions, cheap eats, and insider tips."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-on-a-budget.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="View of Vancouver skyline on a budget"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: INTRODUCTION */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver, Canada, is a gorgeous coastal city known for its
              picturesque mountains, oceanfront views, and vibrant cultural
              scene. While it’s often listed among the most expensive cities
              in Canada, there are countless ways to explore and enjoy
              Vancouver on a tight budget. From scenic parks and free cultural
              experiences to wallet-friendly restaurants, this guide will help
              you make the most of your visit without emptying your savings.
            </p>

            {/* SECTION: FREE & LOW-COST ATTRACTIONS */}
            <h2
              id="free-lowcost"
              ref={(el) => setSpotRef("free-lowcost", el)}
            >
              Free &amp; Low-Cost Attractions
            </h2>
            <p>
              Vancouver boasts an array of spectacular locations you can enjoy
              for free or at minimal cost. Here are some must-visit highlights:
            </p>
            <ul>
              <li>
                <strong>Stanley Park</strong>{" "}
                <p
                  ref={(el) => setSpotRef("stanley-park", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.3000}
                    coordX={-123.1400}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – This iconic 400-hectare urban park offers scenic walking
                  and biking routes along the Seawall, stunning views of the
                  waterfront, and cultural landmarks like the Totem Poles and
                  Brockton Point Lighthouse. Admission is free; plan 2–3 hours
                  for a full loop.
                </p>
              </li>
              <li>
                <strong>Granville Island Public Market</strong>{" "}
                <p
                  ref={(el) => setSpotRef("granville-island", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2726}
                    coordX={-123.1352}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – Home to fresh local produce, artisanal goods, and
                  waterfront entertainment, this indoor market is free to
                  browse. It’s open daily from 9am–6pm.
                </p>
              </li>
              <li>
                <strong>Gastown &amp; Steam Clock</strong>{" "}
                <p
                  ref={(el) => setSpotRef("gastown-clock", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2847}
                    coordX={-123.1110}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – Vancouver’s oldest neighborhood features cobblestone
                  streets, Victorian architecture, and the famous steam clock
                  that whistles every 15 minutes. Perfect for an evening
                  stroll and budget-friendly photo ops.
                </p>
              </li>
              <li>
                <strong>English Bay Beach</strong>{" "}
                <p
                  ref={(el) => setSpotRef("english-bay", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2863}
                    coordX={-123.1436}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – Enjoy a gorgeous sunset, watch street performers, and
                  relax on the sandy shore. Swimming is free, of course, and
                  lifeguards are on duty in summer months.
                </p>
              </li>
              <li>
                <strong>Queen Elizabeth Park</strong>{" "}
                <p
                  ref={(el) => setSpotRef("queen-elizabeth-park", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2417}
                    coordX={-123.1128}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – The highest point in the city, offering panoramic views
                  and beautifully manicured gardens. Free to enter and perfect
                  for a picnic.
                </p>
              </li>
            </ul>

            {/* SECTION: BUDGET-FRIENDLY FOOD */}
            <h2 id="budget-food" ref={(el) => setSpotRef("budget-food", el)}>
              Budget-Friendly Food
            </h2>
            <p>
              Vancouver’s culinary scene can be expensive, but there are
              plenty of affordable eats if you know where to look. Try these
              wallet-friendly spots:
            </p>
            <ul>
              <li>
                <strong>Bon’s Off Broadway</strong>{" "}
                <p
                  ref={(el) => setSpotRef("bon-off-broadway", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2643}
                    coordX={-123.0568}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – Famously known for its $2.95 CAD breakfast special,
                  including eggs, toast, and hash browns. Cash only, but an
                  unbeatable deal.
                </p>
              </li>
              <li>
                <strong>The Famous Warehouse</strong>{" "}
                <p
                  ref={(el) => setSpotRef("warehouse", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.2802}
                    coordX={-123.1211}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – Everything on the menu costs about $5–$7, from burgers
                  and salads to tacos. A popular spot for budget-conscious
                  diners.
                </p>
              </li>
              <li>
                Japadog food truck (various downtown locations): Try a
                Japanese-style hot dog ($7–$9) with teriyaki sauce or seaweed
                flakes. A cheap and unique local favorite.
              </li>
              <li>
                Food Courts &amp; Public Markets: Granville Island’s food
                stalls, Richmond’s food courts, and Chinatown’s bakeries
                (savoury buns for a few dollars) are all great budget
                options.
              </li>
            </ul>

            {/* SECTION: OUTDOOR ACTIVITIES */}
            <h2
              id="outdoor-activities"
              ref={(el) => setSpotRef("outdoor-activities", el)}
            >
              Cheap &amp; Free Outdoor Activities
            </h2>
            <p>
              Vancouver’s natural beauty is unmatched, and many of its best
              outdoor experiences are free or inexpensive:
            </p>
            <ul>
              <li>
                <strong>Lynn Canyon Park &amp; Suspension Bridge</strong>{" "}
                <p
                  ref={(el) => setSpotRef("lynn-canyon", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.3437}
                    coordX={-123.0180}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – A free alternative to Capilano Bridge, it offers stunning
                  forest trails and a thrilling crossing high above the
                  canyon.
                </p>
              </li>
              <li>
                <strong>Hiking the Grouse Grind</strong>{" "}
                <p
                  ref={(el) => setSpotRef("grouse-base", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.3793}
                    coordX={-123.0828}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – The 2.9 km “Mother Nature’s Stairmaster” is free to hike,
                  though the gondola ride down is about $20 if you don’t want
                  to walk back via the BCMC trail.
                </p>
              </li>
              <li>
                Swimming &amp; Beach Days: Most city beaches are free to
                access. Pack a picnic and soak up the scenic views for next to
                no cost.
              </li>
              <li>
                Seawall Biking/Walking: Rent a bike ($10–$15 for a couple of
                hours) or walk the scenic 10 km Stanley Park Seawall.
              </li>
            </ul>

            {/* SECTION: GETTING AROUND */}
            <h2 id="transport" ref={(el) => setSpotRef("transport", el)}>
              Getting Around on a Budget
            </h2>
            <p>
              Vancouver’s public transit system and pedestrian-friendly
              layout make it simple to explore cheaply:
            </p>
            <ul>
              <li>
                <strong>TransLink</strong>: Buses, SkyTrain, and SeaBus
                operate with a single fare system (from $3.20). A DayPass
                (~$11.50) offers unlimited rides.
              </li>
              <li>
                <strong>Airport to Downtown</strong>{" "}
                <p
                  ref={(el) => setSpotRef("airport-canada-line", el)}
                  style={{ scrollMarginTop: "80px" }}
                >
                  <Marker
                    coordY={49.1947}
                    coordX={-123.1770}
                    mobileMapRef={mobileMapRef}
                    mapRef={mapRef}
                  />
                  – The Canada Line SkyTrain from Vancouver International
                  Airport (YVR) to downtown costs around $9 (including a $5
                  surcharge).
                </p>
              </li>
              <li>
                Biking: Try the public bike-share system called Mobi, with
                24-hour passes for around $12.
              </li>
              <li>
                Walking: Vancouver’s downtown is compact and easily walkable.
              </li>
            </ul>

            {/* SECTION: SEASONAL TIPS */}
            <h2 id="seasonal" ref={(el) => setSpotRef("seasonal", el)}>
              Seasonal Considerations
            </h2>
            <p>
              Vancouver’s offerings change with the seasons, which can impact
              both your itinerary and travel costs:
            </p>
            <ul>
              <li>
                <strong>Summer</strong>: Peak tourist season, but free
                festivals like the Celebration of Light fireworks can offset
                higher accommodation prices.
              </li>
              <li>
                <strong>Fall</strong>: Moderate weather, fewer crowds, and
                beautiful foliage; a good time for deals.
              </li>
              <li>
                <strong>Winter</strong>: Mild but rainy in the city; cheaper
                hotel rates. Free ice skating at Robson Square (rentals ~$5).
              </li>
              <li>
                <strong>Spring</strong>: Cherry blossoms and moderate prices;
                ideal for outdoor exploration before peak summer crowds.
              </li>
            </ul>

            {/* SECTION: SUGGESTED ITINERARIES */}
            <h2 id="itineraries" ref={(el) => setSpotRef("itineraries", el)}>
              Suggested Itineraries
            </h2>
            <p>
              Whether you have a day or a full week, here are some sample
              itineraries:
            </p>
            <strong>1-Day:</strong>
            <ul>
              <li>Morning in Gastown, Steam Clock, and nearby coffee shops</li>
              <li>Afternoon biking Stanley Park Seawall</li>
              <li>Evening at English Bay Beach for sunset</li>
            </ul>
            <strong>3-Day:</strong>
            <ul>
              <li>Day 1: Downtown &amp; Stanley Park</li>
              <li>Day 2: North Vancouver (Lynn Canyon, Grouse Grind)</li>
              <li>Day 3: Granville Island and Kitsilano Beach</li>
            </ul>
            <strong>1-Week:</strong>
            <ul>
              <li>
                Incorporate a day trip to Whistler or Victoria via bus/ferry,
                plus time for neighborhoods like Chinatown, Commercial Drive,
                and UBC.
              </li>
            </ul>

            {/* SECTION: LOCAL REGULATIONS */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
            >
              Local Regulations
            </h2>
            <p>
              Vancouver has strict smoking bylaws (no smoking in parks or
              beaches), mandatory bike helmet laws, and regulations against
              feeding wildlife in parks. Cannabis is legal for adults 19+ but
              prohibited in public parks. Check official sites for updated
              info on alcohol consumption zones and other local bylaws.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. When is the best time to visit Vancouver on a budget?</h3>
              <p>
                The off-peak seasons (fall and winter) generally offer lower
                hotel and flight prices. Spring is a good compromise between
                decent weather and moderate costs.
              </p>

              <h3>2. Do I need a car to get around Vancouver?</h3>
              <p>
                Not necessarily. Vancouver’s public transit system (buses,
                SkyTrain, SeaBus) is extensive. Most neighborhoods are quite
                walkable. Rent a car only if you plan day trips to regions
                with limited public transport.
              </p>

              <h3>3. What’s the cheapest way to go from the airport to downtown?</h3>
              <p>
                Take the Canada Line SkyTrain from YVR for about $9. It’s fast
                (25 minutes), and you’ll avoid the ~$35 taxi fare.
              </p>
            </div>

            {/* SECTION: CONCLUSION */}
            <h2
              id="conclusion"
              ref={(el) => setSpotRef("conclusion", el)}
            >
              Conclusion
            </h2>
            <p>
              Traveling on a budget in Vancouver is entirely possible with the
              right strategies. Focus on the city’s abundance of free natural
              beauty, sample affordable local eats, and leverage public
              transit. With these tips, you’ll have a memorable experience
              without overspending. Always verify operating hours, fares, and
              any bylaw changes on official websites. Enjoy your trip!
            </p>
            <p>
              <em>
                Disclaimer: Always check local guidelines, safety regulations,
                and updated transit fares before planning your itinerary.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}